import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import API from "../api";
import { socket } from "../socket";
import { useNavigate } from "react-router-dom";
import "../styles/notifications.css";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const nav = useNavigate();
  const panelRef = useRef(null);

  const unread = useMemo(
    () => unreadCount || items.filter((x) => !x.isRead).length,
    [items, unreadCount]
  );

  async function load() {
    try {
      setLoading(true);
      const res = await API.get("/notifications");

      setItems(res.data?.items || []);
      setUnreadCount(res.data?.unreadCount || 0);
    } catch (err) {
      console.error("Load notifications error:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();

    const onNew = (doc) => {
      setItems((prev) => [doc, ...prev]);
      setUnreadCount((c) => c + 1);
    };

    socket.on("notification:new", onNew);
    return () => socket.off("notification:new", onNew);
  }, []);

  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  async function markRead(id) {
    setItems((prev) =>
      prev.map((n) => (n._id === id ? { ...n, isRead: true } : n))
    );
    setUnreadCount((c) => Math.max(0, c - 1));

    try {
      await API.patch(`/notifications/${id}/read`);
    } catch {}
  }

  async function markUnread(id) {
    setItems((prev) =>
      prev.map((n) => (n._id === id ? { ...n, isRead: false } : n))
    );
    setUnreadCount((c) => c + 1);

    try {
      await API.patch(`/notifications/${id}/unread`);
    } catch {}
  }

  async function dismiss(id) {
    setItems((prev) => prev.filter((n) => n._id !== id));
    try {
      await API.delete(`/notifications/${id}`);
    } catch {}
  }

  async function dismissRead() {
    setItems((prev) => prev.filter((n) => !n.isRead));
    try {
      await API.delete(`/notifications/read`);
    } catch {}
  }

  async function markAllRead() {
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);

    try {
      await API.patch(`/notifications/read-all`);
    } catch {}
  }

  function openNotif(n) {
    if (!n.isRead) markRead(n._id);
    setOpen(false);
    nav(n.link || "/dashboard");
  }

  function getTypeLabel(type) {
    if (!type) return "general";
    return type.replace("_", " ");
  }

  function getIcon(type) {
    switch (type) {
      case "claim": return <i className="fa-solid fa-file-invoice-dollar" style={{ color: "var(--brand-400)" }} />;
      case "appointment_status":
      case "appointment": return <i className="fa-solid fa-calendar-check" style={{ color: "var(--brand-400)" }} />;
      case "pharmacy": return <i className="fa-solid fa-pills" style={{ color: "var(--brand-400)" }} />;
      case "security": return <i className="fa-solid fa-shield-halved" style={{ color: "#f87171" }} />;
      default: return <i className="fa-solid fa-bell" style={{ color: "var(--brand-400)" }} />;
    }
  }

  return (
    <div className="notif-wrap" ref={panelRef}>
      <motion.button
        className="icon-btn notif-btn"
        onClick={() => setOpen((v) => !v)}
        whileTap={{ scale: 0.95 }}
        aria-label="Notifications"
      >
        <i className="fa-regular fa-bell" />
        {unread > 0 && (
          <span className="notif-badge">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="notif-panel"
            initial={{ opacity: 0, y: 12, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.96 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* HEADER */}
            <div className="notif-head">
              <div className="notif-head-left">
                <div className="notif-title">Notifications</div>
                <div className="notif-subtitle">
                  {unread} unread activities
                </div>
              </div>

              <div className="notif-head-actions">
                <button
                  className="notif-link"
                  onClick={markAllRead}
                >
                  <i className="fa-solid fa-check-double" /> Mark all read
                </button>
              </div>
            </div>

            {/* LIST */}
            <div className="notif-list">
              {loading ? (
                <div className="notif-empty">
                  <div className="ms-skel" style={{ width: 40, height: 40, margin: '0 auto 12px', borderRadius: '50%' }} />
                  Securely retrieving updates...
                </div>
              ) : items.length === 0 ? (
                <div className="notif-empty">
                  <i className="fa-solid fa-moon" style={{ fontSize: 32, opacity: 0.2, marginBottom: 12, display: 'block' }} />
                  System clear. No new alerts.
                </div>
              ) : (
                items.map((n) => (
                  <SwipeRow
                    key={n._id}
                    onDismiss={() => dismiss(n._id)}
                  >
                    <div
                      className={`notif-item ${
                        n.isRead ? "read" : "unread"
                      }`}
                      onClick={() => openNotif(n)}
                      role="button"
                    >
                      <div className="notif-item-top">
                        <div style={{ marginTop: 2 }}>{getIcon(n.type)}</div>
                        <div className="notif-item-main">
                          <div className="notif-item-title">
                            {!n.isRead && <span className="notif-dot" />}
                            {n.title}
                            {n.type && (
                              <span className="notif-tag">
                                {getTypeLabel(n.type)}
                              </span>
                            )}
                          </div>

                          <div className="notif-item-msg">
                            {n.message}
                          </div>

                          <div className="notif-meta">
                            <div className="notif-time">
                              {new Date(n.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </div>

                            <div className="notif-item-actions">
                              {!n.isRead ? (
                                <button
                                  className="notif-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markRead(n._id);
                                  }}
                                >
                                  Mark read
                                </button>
                              ) : (
                                <button
                                  className="notif-link"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    markUnread(n._id);
                                  }}
                                >
                                  Mark unread
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        <button
                          className="notif-x"
                          onClick={(e) => {
                            e.stopPropagation();
                            dismiss(n._id);
                          }}
                          aria-label="Dismiss"
                        >
                          <i className="fa-solid fa-xmark" />
                        </button>
                      </div>
                    </div>
                  </SwipeRow>
                ))
              )}
            </div>

            {/* FOOTER */}
            {items.length > 0 && (
              <div className="notif-foot">
                <div className="notif-foot-note">
                  <i className="fa-solid fa-shield-halved" /> Security synced
                </div>

                <button
                  className="notif-danger-link"
                  onClick={dismissRead}
                >
                  <i className="fa-solid fa-trash-can" /> Clear read
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Mobile swipe-to-dismiss */
function SwipeRow({ children, onDismiss }) {
  const [dx, setDx] = useState(0);
  const startX = useRef(0);
  const dragging = useRef(false);

  function onTouchStart(e) {
    dragging.current = true;
    startX.current = e.touches[0].clientX;
  }

  function onTouchMove(e) {
    if (!dragging.current) return;
    const cur = e.touches[0].clientX;
    const diff = cur - startX.current;
    setDx(Math.min(0, diff));
  }

  function onTouchEnd() {
    dragging.current = false;
    if (dx < -90) onDismiss();
    setDx(0);
  }

  return (
    <div
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      style={{
        transform: `translateX(${dx}px)`,
        transition: dragging.current
          ? "none"
          : "transform 150ms ease",
      }}
    >
      {children}
    </div>
  );
}